import { gql } from '@apollo/client';

import { BASE_TASK, BASE_TASK_ITEM } from '@atom/graph/task';

export const BASE_WORK_ORDER_FIELD = gql`
  fragment BaseWorkOrderField on WorkOrderField {
    id
    dataType
    title
    required
    value
    enumeration
    defaultValue
    subFieldsTruncated
    subFields {
      matchValue
      nestedField
    }
    units
    hyperlink
  }
`;

export const BASE_WORK_ORDER = gql`
  fragment BaseWorkOrder on WorkOrder {
    id
    type
    name
    originalName
    inventoryAssetId
    inventoryAssetName
    inventoryAssetSchemaId
    inventoryAssetLocation
    inventoryAssetAddress
    inventoryAssetMarkerId
    inventoryAssetActions
    inventoryAssetGrants {
      type
      ids
    }
    isClosed
    dueDate
    completionDate
    startTime
    duration
    workTime
    actualCost
    estimatedCost
    reopened
    appointmentsEnabled
    plannedStartDate
    reopenLocked
    source
    pmTemplateId

    workTemplateName
    workTemplateFolderPath {
      name
    }
    workTemplateFolderActions
    workTemplateFolderGrants {
      type
      ids
    }
    workTemplateId
    workTemplate {
      id
      name
      schemaId
    }

    location
    isLocationBased

    statusId
    priorityId

    mainPhotoId
    mainPhotoFileId

    leadAssigneeId
    leadAssigneeName
    leadAssigneeUser {
      id
      firstName
      lastName
      employeeId
    }

    closedDate
    closedByName
    closedByUser {
      id
      firstName
      lastName
      employeeId
    }

    appliedBy
    appliedByName
    appliedDate

    completedBy
    completedByName
    completedByUser {
      id
      firstName
      lastName
      employeeId
    }

    createdBy
    createdByName
    createdDate

    assets
    multiAssets

    formInstances

    summary {
      title
      contents
    }

    fieldOrder
    fields {
      ...BaseWorkOrderField
    }

    tasks {
      ...BaseTask
    }
  }
  ${BASE_TASK}
  ${BASE_WORK_ORDER_FIELD}
`;

export const GET_WORK_ORDER = gql`
  query getWorkOrder($id: ID!, $showAdmin: Boolean) {
    workOrder(id: $id, showAdmin: $showAdmin) {
      ...BaseWorkOrder
    }
  }
  ${BASE_WORK_ORDER}
`;

export const GET_WORK_ORDERS = gql`
  query workOrders($input: WorkOrdersConnectionInput!) {
    workOrders(input: $input) {
      totalCount
      workOrders {
        id
        type
        name
        originalName
        priorityId
        statusId
        reopened
        appointmentsEnabled
        plannedStartDate

        inventoryAssetId
        inventoryAssetName
        inventoryAssetSchemaId
        appliedDate
        reopenLocked

        workTemplateId
        workTemplate {
          id
          name
        }

        startTime
        duration
        dueDate
        completionDate
        completedBy {
          id
          firstName
          lastName
          email
          photoUrl
        }

        fields {
          ...BaseWorkOrderField
        }

        tasks {
          ...BaseTaskItem
        }

        createdDate
        createdBy {
          id
          firstName
          lastName
          email
          photoUrl
        }
        source
        pmTemplateId
      }
    }
  }
  ${BASE_TASK_ITEM}
  ${BASE_WORK_ORDER_FIELD}
`;

export const WORK_ORDER_CREATE = gql`
  mutation workOrderCreate($input: WorkOrderCreateInput!) {
    workOrderCreate(input: $input) {
      ...BaseWorkOrder
    }
  }
  ${BASE_WORK_ORDER}
`;

export const WORK_ORDER_UPDATE = gql`
  mutation workOrderUpdate($workOrder: WorkOrderUpdateInput!) {
    workOrderUpdate(workOrder: $workOrder) {
      id
      name
      dueDate

      location
      isLocationBased

      statusId
      priorityId

      completionDate
      leadAssigneeId
      mainPhotoId
    }
  }
`;

export const GET_WORK_REPORTS = gql`
  query getWorkReports($id: ID!) {
    workReports(id: $id) {
      totalCount
      workReports {
        workOrderId
        taskId
        acctId
        divId
        distId
        roadwayClassId
        funcId
      }
    }
  }
`;

export const WORK_ORDER_DELETE = gql`
  mutation workOrderDelete($id: ID!) {
    workOrderDelete(id: $id)
  }
`;

export const WORK_ORDER_DUPLICATE = gql`
  mutation workOrderDuplicate($id: ID!) {
    workOrderDuplicate(id: $id)
  }
`;

export const WORK_ORDER_BULK_CREATE = gql`
  mutation workOrderBulkCreate($input: WorkOrderBulkCreateInput!) {
    workOrderBulkCreate(input: $input) {
      totalAssets
      totalWorkOrders
    }
  }
`;

export const WORK_ORDER_BULK_ASSET_FILTER_CREATE = gql`
  mutation workOrderBulkAssetFilterCreate(
    $input: WorkOrderBulkAssetFilterCreateInput!
  ) {
    workOrderBulkAssetFilterCreate(input: $input) {
      totalAssets
      totalWorkOrders
    }
  }
`;

export const WORK_ORDER_BULK_MAP_CREATE = gql`
  mutation workOrderBulkMapCreate($input: WorkOrderBulkMapCreateInput!) {
    workOrderBulkMapCreate(input: $input)
  }
`;

export const WORK_ORDER_FIELD_CREATE = gql`
  mutation workOrderFieldCreate($input: WorkOrderFieldCreateInput!) {
    workOrderFieldCreate(input: $input) {
      ...BaseWorkOrderField
    }
  }
  ${BASE_WORK_ORDER_FIELD}
`;

export const WORK_ORDER_FIELD_UPDATE = gql`
  mutation workOrderFieldUpdate($input: WorkOrderFieldUpdateInput!) {
    workOrderFieldUpdate(input: $input) {
      ...BaseWorkOrderField
    }
  }
  ${BASE_WORK_ORDER_FIELD}
`;

export const WORK_ORDER_FIELD_DELETE = gql`
  mutation workOrderFieldDelete($input: WorkOrderFieldDeleteInput!) {
    workOrderFieldDelete(input: $input)
  }
`;

export const GET_WORK_ORDER_HYDRATED = gql`
  query getHydratedWorkOrder(
    $id: ID!
    $mediaInput: WorkOrderMediaConnectionInput!
  ) {
    workOrderHydrated(id: $id) {
      id
      type
      name
      originalName
      inventoryAssetId
      inventoryAssetName
      inventoryAssetSchemaId
      inventoryAssetLocation
      inventoryAssetAddress
      inventoryAssetMarkerId
      inventoryAssetActions
      inventoryAssetGrants {
        type
        ids
      }

      isClosed
      reopened
      dueDate
      completionDate
      estimatedCost
      startTime
      duration
      workTime
      actualCost
      appointmentsEnabled
      plannedStartDate

      workTemplateName
      workTemplateId
      workTemplateFolderPath {
        id
        name
      }

      location
      isLocationBased

      statusId
      priorityId

      mainPhotoId
      mainPhotoFileId

      leadAssigneeId
      leadAssigneeName

      closedDate
      closedByName

      appliedBy
      appliedByName
      appliedDate

      completedBy
      completedByName

      createdBy
      createdByName
      createdDate

      assets
      multiAssets

      formInstances

      summary {
        title
        contents
      }

      media(input: $mediaInput) {
        media {
          id
          name
          type
          fileId
          fileExtension
          capturedDate
          location {
            type
            coordinates
          }
          description
          parentSubjectId
          subjectId
          subjectType
          folderId
          folderPath
          createdDate
          updatedDate
          createdBy {
            id
            firstName
            lastName
            photoUrl
          }
          updatedBy {
            id
            firstName
            lastName
            photoUrl
          }
        }
      }

      fields {
        id
        dataType
        title
        required
        value
        enumeration
        defaultValue
        subFieldsTruncated
        subFields {
          matchValue
          nestedField
        }
        units
        hyperlink
      }

      tasks {
        id
        name
        originalName
        duration
        dueDate
        completionDate
        completedBy
        completedByName
        description
        estimatedCost
        isCompleted
        updatedDate
        createdDate
        startTime
        workTime
        actualCost
        isEstimatedCostOverridden
        taskTemplateId

        assetIds
        formInstanceIds

        assets {
          id
          name
          schemaId
          isNew
          ancestors {
            id
            name
          }
          categoryPath {
            id
            name
          }
          createdDate
          updatedDate
        }

        formInstances {
          id
          name
          isValid
          formTemplateId
        }

        users {
          id
          employeeId
          baseLocation
          firstName
          lastName
          isVerified
          isActive
          photoUrl
          department
          title
          role
          level
          rate
          email
          location
          createdDate
          updatedDate
          preferences
          status
          notifications
          workTime
          actualCost

          budget {
            budgetId
            name
            rate
          }
        }

        materials {
          name
          assetId
          colorId
          isStockBased
          isStartEndReading
          isStartEndCalculated
          rate
          cost
          quantity
          unit
        }

        userGroups {
          userGroupId
          name
          colorId
          quantity
          workTime
          budget {
            budgetId
            name
            rate
            restored
            reopenedWorkOrderIds
          }
        }

        fields {
          id
          dataType
          title
          required
          value
          enumeration
          defaultValue
          subFieldsTruncated
          subFields {
            matchValue
            nestedField
          }
          units
          hyperlink
        }

        categoryIds
        categories {
          id
          name
          schemaId
          assetType
          categoryPath
          parentCategoryId
        }

        locations {
          id
          name
        }

        locationsEnabled
        requireAtLeastOneLocation
        multiAssetsEnabled
        allowedMultiAssetSchemaIds
        requireAtLeastOneMultiAsset
        requiredMultiAssetSchemaIds
      }
    }
  }
`;
